<template>
  <div>
    <div class="mb-2 flex justify-end">
      <ExportMenu :disabled="!rowData.length" :exports="[{ type: 'excel', action: exportToExcel }]" />
    </div>
    <div :class="[!autoHeight ? 'h-96':'']">
      <AgGridVue
        :theme="standardListTheme"
        style="width: 100%; height: 100%"
        :column-defs="columnDefs"
        :row-data="rowData"
        :default-col-def="defaultColDef"
        :auto-group-column-def="autoGroupColumnDef"
        :grid-options="gridOptions"
        :loading="loading"
        @grid-ready="onGridReady"
        :treeData="true"
        :treeDataParentIdField="'parentId'"
        :getRowId="getRowId"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import {standardListTheme} from '@/utils/common';
import CC_Router_Link_To_Entity from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue";
import CC_Engagement_Score_Badge from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Engagement_Score_Badge.vue";
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';

export default {
  components: {
    AgGridVue,
    CC_Router_Link_To_Entity,
    CC_Engagement_Score_Badge,
    ExportMenu
  },
  props: {
    autoHeight: {
      type: Boolean,
      required: false,
      default: false
    },
    rootEntityId: {
      type: Number,
      required: false,
      default: null
    },
    entityTypeName: {
      type: String,
      required: false,
      default: null
    },
    scoreFilter: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        domLayout: this.autoHeight ? 'autoHeight' : null,
        animateRows: true,
      },
      loading: true,
      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        resizable: true,
      },
      autoGroupColumnDef: {
        headerName: 'Name',
        field: 'name',
        cellRendererParams: {
          suppressCount: true,
          // Add inner renderer for the custom link component
          innerRenderer: 'CC_Router_Link_To_Entity',
          innerRendererParams: {
            suppressCount: true
          }
        },
        sort: 'asc',
        minWidth: 300,
      },
      columnDefs: [
        {
          headerName: 'Attribution',
          field: 'attribution.total_attribution',
          cellClass: 'text-right',
          valueGetter: params => {
            return params.data?.attribution?.total_attribution || 0;
          }
        },
        {
          headerName: 'Last Engagement',
          field: 'last_engagement_date',
          cellClass: 'text-right',
          width: 180,
          valueFormatter: params => {
            if (!params.data?.last_engagement_date) return '';
            const [y, m, d] = params.data.last_engagement_date.split('-');
            const date = new Date(y, m - 1, d);
            const days = Math.floor((Date.now() - date) / 86400000);
            return `${m}/${d}/${y.slice(-2)} (${days} days)`;
          },
          cellStyle: params => {
            if (!params.data?.last_engagement_date) return null;
            const [y, m, d] = params.data.last_engagement_date.split('-');
            const days = Math.floor((Date.now() - new Date(y, m - 1, d)) / 86400000);
            return days > 90 ? { color: '#f97316' } : null;
          }
        },
        {
          headerName: '# Engagements',
          field: 'num_engagements',
          cellClass: 'text-right',
          valueGetter: params => {
            const calculateSum = node => {
              if (!node.childrenAfterGroup || node.childrenAfterGroup.length === 0) {
                // Leaf node, return its own value
                return node.data?.num_engagements || 0;
              }
              // Parent node, sum its own value and recursively sum children
              const childrenSum = node.childrenAfterGroup.reduce((sum, child) => sum + calculateSum(child), 0);
              const parentValue = node.data?.num_engagements || 0;
              return parentValue + childrenSum;
            };

            return calculateSum(params.node);
          }
        },
        {
          headerName: 'Engagement Score',
          field: 'engagement_score',
          cellRenderer: 'CC_Engagement_Score_Badge',
          width: 140,
          valueGetter: params => {
            // Return full object for cell renderer (has sub_scores for tooltip)
            return params.data?.engagement_score
          },
          valueFormatter: params => {
            // For Excel export, just show the composite score
            const score = params.value?.composite
            return score != null ? Math.round(score) : ''
          }
        },
        {
          headerName: 'Type',
          field: 'entity_type',
          width: 120,
        },
      ],
      rowData: [],
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme;
    },
  },
  methods: {
    getRowId(params) {
      // Use composite key to handle entities that appear under multiple parents
      // Format: {id}_{parentCompositeKey} for unique identification at any depth
      const parentKey = params.data.parentId ?? 'root';
      return `${params.data.id}_${parentKey}`;
    },

    async onGridReady(params) {
      this.gridApi = params.api;
      await this.loadData();
      params.api.sizeColumnsToFit();
    },

    async loadData() {
      this.loading = true;
      try{
        const response = await axios.get('/api/entities/entity_tree/',{
          params: {
            root_entity_id: this.rootEntityId,
            entity_type_name: this.entityTypeName,
            include_attribution: true,
          }
        }
        );

        let data = response.data

        // Apply score filter if provided
        if (this.scoreFilter) {
          data = data.filter(row => {
            const score = row.engagement_score?.composite
            if (score == null) return false
            return score >= this.scoreFilter.min && score < this.scoreFilter.max
          })
        }

        // Transform data to handle entities appearing under multiple parents
        // When an entity appears multiple times (e.g., shared Location), its children
        // must be replicated under each parent instance, recursively

        // Group rows by their original parentId
        const childrenOf = new Map()
        for (const row of data) {
          const key = row.parentId ?? null
          if (!childrenOf.has(key)) childrenOf.set(key, [])
          childrenOf.get(key).push(row)
        }

        // Track composite keys for each original id (built as we process)
        const compositeKeysOf = new Map()
        compositeKeysOf.set(null, [null]) // Root's "parent" is null

        // Process level by level so parents are expanded before children
        const expandedRows = []
        const queue = [null]
        const queued = new Set([null])

        while (queue.length > 0) {
          const parentId = queue.shift()
          const parentKeys = compositeKeysOf.get(parentId) || []

          for (const row of childrenOf.get(parentId) || []) {
            // Create a row for each instance of the parent
            for (const parentKey of parentKeys) {
              const compositeKey = `${row.id}_${parentKey ?? 'root'}`
              expandedRows.push({ ...row, parentId: parentKey })
              if (!compositeKeysOf.has(row.id)) compositeKeysOf.set(row.id, [])
              compositeKeysOf.get(row.id).push(compositeKey)
            }

            // Queue children for processing
            if (childrenOf.has(row.id) && !queued.has(row.id)) {
              queue.push(row.id)
              queued.add(row.id)
            }
          }
        }

        this.rowData = expandedRows
      } catch (error) {
        console.error('Error fetching data:', error);
        this.rowData = [];
      } finally {
        this.loading = false;
      }
    },

    exportToExcel() {
      if (this.gridApi) {
        if (this.$root?.isDemoMode) return; // Demo Mode: block raw-data export
        this.gridApi.exportDataAsExcel({
          allColumns: false,
          columnKeys: ['ag-Grid-AutoColumn', 'attribution.total_attribution', 'last_engagement_date', 'num_engagements', 'engagement_score', 'entity_type']
        });
      }
    },
  },

  watch: {
    rootEntityId() {
      this.loadData();
    },
    entityTypeName() {
      this.loadData();
    },
  }
};
</script>

<style scoped>
</style>
